import { commsText } from './alert-copy';
// ---------------------------------------------------------------------------
// DM display helpers (pure — no React, no SDK).
//
// Context: when a DM channel is created, the backend persists `name` as fixed
// by the CREATOR (typically the recipient's name). That means the RECIPIENT
// sees the DM labeled with their OWN name. These helpers correct that
// client-side: for a `type === 'direct'` channel we resolve the counterpart
// member (the member that is not me) and derive the display name from them,
// only falling back to the stored `channel.name` when nothing better exists.
//
// Q7 (backend PR #10): `channelToDTO` now computes a PER-VIEWER
// `display_name` for type='direct' channels — the counterpart's name from
// the server's perspective (empty/omitted on non-direct channels). When
// present it is the most authoritative source, so `dmDisplayName` prefers it
// ABOVE everything: server display_name → counterpart user_name → directory
// → stored name → fallback. The client-side chain stays as a fallback for
// responses that predate the field (WS payloads, older caches).
// ---------------------------------------------------------------------------

export interface DmMember {
  user_id?: string;
  user_name?: string;
  user_avatar?: string;
}

export interface DmChannelLike {
  type?: string;
  name?: string;
  /** Per-viewer counterpart name computed server-side for DMs (Q7). */
  display_name?: string;
  members?: DmMember[] | null;
}

/** Directory lookup: either a Map of user_id → display name, or a resolver fn. */
export type DirectoryLookup = Map<string, string> | ((id: string) => string | undefined);

/**
 * The member on the other side of a direct (1:1) channel.
 *
 * Returns null when not determinable: non-direct channel, missing/empty
 * members, or every member is me / lacks a user_id. With dirty data (more
 * than one non-me member) the first one wins.
 */
export function dmCounterpart(
  channel: DmChannelLike,
  currentUserId: string | null,
): DmMember & { user_id: string } | null {
  if (channel.type !== 'direct') return null;
  const members = channel.members ?? [];
  for (const m of members) {
    if (m?.user_id && m.user_id !== currentUserId) {
      return { ...m, user_id: m.user_id };
    }
  }
  return null;
}

function lookupDirectory(
  directory: DirectoryLookup | undefined,
  userId: string,
): string | undefined {
  if (!directory) return undefined;
  if (typeof directory === 'function') return directory(userId) || undefined;
  return directory.get(userId) || undefined;
}

/**
 * Display name for a DM channel from the CURRENT user's perspective.
 *
 * Priority: server-computed `display_name` (per-viewer, Q7) → counterpart's
 * `user_name` → directory lookup by `user_id` → stored `channel.name` →
 * 'Mensaje directo'.
 */
export function dmDisplayName(
  channel: DmChannelLike,
  currentUserId: string | null,
  directory?: DirectoryLookup,
  fallback = commsText('direct'),
): string {
  // The server already resolved the counterpart for THIS viewer — trust it.
  const serverName = channel.display_name?.trim();
  if (serverName) return serverName;
  const counterpart = dmCounterpart(channel, currentUserId);
  if (counterpart) {
    const memberName = counterpart.user_name?.trim();
    if (memberName) return memberName;
    const dirName = lookupDirectory(directory, counterpart.user_id)?.trim();
    if (dirName) return dirName;
  }
  const stored = channel.name?.trim();
  if (stored) return stored;
  return fallback;
}
